import type { PostHog } from 'posthog-js';
import { filterUnactionableExceptions } from '@/providers/posthog-filters';

// posthog-js is ~56 KB gzip and was statically imported by 50+ modules, so it
// shipped in the shared bundle of every page. This shim has the same call
// shape for the methods the app uses; calls made before the SDK loads are
// queued (with their original timestamp) and replayed once it has.
type Call = (ph: PostHog) => void;

let client: PostHog | null = null;
let loading: Promise<PostHog | null> | null = null;
let disabled = false;
// Set by ready(): the caller is about to leave the page, so captures skip the
// SDK's batch queue and go out over sendBeacon, which survives the unload.
let navigating = false;
let navigatingReset: ReturnType<typeof setTimeout> | undefined;
const queue: Call[] = [];

/** Capture options for an event that is immediately followed by a hard navigation. */
export const BEACON_OPTIONS = {
  send_instantly: true,
  transport: 'sendBeacon',
} as const;

function run(call: Call) {
  // SDK is browser-only (no-op on the server); `disabled` means no project key.
  if (typeof window === 'undefined' || disabled) return;
  if (client) {
    // Every call reaches a loaded SDK on the next microtask, so calls stay in
    // order (capture before identify) and a `capture(); ready()` pair in one
    // synchronous block sees navigation mode when the capture is sent.
    const ph = client;
    queueMicrotask(() => call(ph));
  } else {
    queue.push(call);
  }
}

export function loadPostHog(): Promise<PostHog | null> {
  loading ??= import('posthog-js').then(
    ({ default: ph }) => {
      const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
      if (!key) {
        console.error(
          'PostHog: NEXT_PUBLIC_POSTHOG_KEY is not set; analytics disabled.'
        );
        disabled = true;
        queue.length = 0;
        return null;
      }
      try {
        ph.init(key, {
          api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
          person_profiles: 'identified_only',
          capture_pageview: false,
          capture_pageleave: true,
          before_send: filterUnactionableExceptions,
        });
      } catch (error) {
        console.error('PostHog: init failed; analytics disabled.', error);
        disabled = true;
        queue.length = 0;
        return null;
      }
      client = ph;
      // One bad replayed call must not stop the rest (or reject the loader).
      for (const call of queue.splice(0)) {
        try {
          call(ph);
        } catch (error) {
          console.error('PostHog: replaying a queued call failed', error);
        }
      }
      return ph;
    },
    (error: unknown) => {
      console.error('PostHog: failed to load the SDK chunk', error);
      loading = null; // allow a later retry
      return null;
    }
  );
  return loading;
}

type CaptureArgs = Parameters<PostHog['capture']>;

const posthog = {
  capture(
    event: CaptureArgs[0],
    properties?: CaptureArgs[1],
    options?: CaptureArgs[2]
  ) {
    const timestamp = new Date();
    // Beacon vs batch is decided when the call reaches the SDK (see run()).
    run((ph) =>
      ph.capture(event, properties, {
        timestamp,
        ...(navigating ? BEACON_OPTIONS : {}),
        ...options,
      })
    );
  },
  captureException(...args: Parameters<PostHog['captureException']>) {
    run((ph) => ph.captureException(...args));
  },
  identify(...args: Parameters<PostHog['identify']>) {
    run((ph) => ph.identify(...args));
  },
  /**
   * Call right before a hard navigation (location.href/replace, OAuth). Resolves
   * once the SDK is loaded and queued events are handed to it, or after
   * `timeoutMs`, whichever is first; never rejects. Until then, and for a few
   * seconds after, captures are sent instantly over sendBeacon so they survive
   * the unload. The timeout keeps a blocked or slow SDK from holding up the
   * navigation.
   */
  ready(timeoutMs = 1500): Promise<void> {
    if (disabled || typeof window === 'undefined') return Promise.resolve();
    navigating = true;
    clearTimeout(navigatingReset); // a re-arm restarts the window
    navigatingReset = setTimeout(() => {
      navigating = false; // the navigation didn't happen; back to batching
    }, 5000);
    if (client) return Promise.resolve();
    return Promise.race([
      // Never rejects: an SDK that throws on init or replay must not break the caller.
      loadPostHog().then(
        () => undefined,
        () => undefined
      ),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  },
};

export default posthog;
